import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../hooks/redux-hooks";
import {
  getAppConfigI18nByKey,
  searchOrderById,
  type AppConfigI18nData,
} from "../slices/authSlice";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "../utils/sessionStorage";
// import { format } from "date-fns";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import * as consts from "../constants";
import blueskyImage from '../assets/bluesky.png';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 配置 dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

const CREDIT_CARD_FEE_KEY = "payment.credit_card_fee";
const CREDIT_CARD_FEE_SESSION_KEY = "currentOrder.payment.credit_card_fee";
const CREDIT_CARD_FEE_SESSION_TTL = 30 * 60 * 1000;

const getCreditCardFeeText = (
  items: AppConfigI18nData[],
  language: string,
  fallbackText: string
) => {
  if (!Array.isArray(items) || items.length === 0) {
    return fallbackText;
  }

  const normalizedLanguage = language.toLowerCase();
  const matchedItem =
    items.find((item) => item.locale.toLowerCase() === normalizedLanguage) ||
    (normalizedLanguage.startsWith("zh")
      ? items.find((item) => item.locale.toLowerCase().startsWith("zh"))
      : items.find((item) => item.locale.toLowerCase().startsWith("en"))) ||
    items[0];

  return matchedItem?.value || fallbackText;
};

const styles = {
  backButton: {
    position: "absolute",
    left: 16,
    top: 16,
    color: "text.secondary",
    "&:hover": {
      color: "primary.main",
    },
  },
  headerContainer: {
    position: "relative",
    width: "100%",
    mb: 3,
  },
  container: {
    p: 3,
  },
  noOrderContainer: {
    p: 3,
    textAlign: "center",
  },
  gridContainer: {
    spacing: 3,
    px: 2, // 添加左右内边距
  },
  leftGridContainer: {
    maxWidth: "90%", // 控制最大宽度
    mx: "auto", // 水平居中
  },
  leftGrid: {
    xs: 12,
    md: 7,
  },
  rightGrid: {
    xs: 12,
    md: 5,
  },
  title: {
    mb: 3,
  },
  mainTable: {
    mb: 3,
  },
  smallTable: {
    width: "80%",
    mx: "auto",
    mb: 3,
  },
  headerCell: {
    width: "20%",
    bgcolor: "grey.200",
  },
  normalHeaderCell: {
    bgcolor: "grey.200",
  },
  addressCell: {
    whiteSpace: "pre-wrap",
    WebkitTextSizeAdjust: "100%",
    textSizeAdjust: "100%",
  },
  paymentTable: {
    border: "1px solid rgba(0, 0, 0, 0.2)",
  },
  paymentHeaderCell: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
  },
  paymentLeftCell: {
    width: "50%",
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
    borderRight: "1px solid rgba(0, 0, 0, 0.2)",
  },
  paymentRightCell: {
    width: "50%",
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
  },
  paymentLastLeftCell: {
    borderRight: "1px solid rgba(0, 0, 0, 0.2)",
  },
  contactFirstCell: {
    border: "none",
    pb: 0,
  },
  contactMiddleCell: {
    border: "none",
    py: 1,
  },
  contactLastCell: {
    border: "none",
    pt: 1,
  },
  policyContainer: {
    maxWidth: "90%", // 控制最大宽度
    mx: "auto", // 水平居中
    p: 3,
  },
  policyContent: {
    mt: 3,
  },
  policyDivider: {
    my: 2,
  },
  policyText: {
    mt: 2,
  },
  policyNote: {
    mt: 1,
  },
  redText: {
    color: "error.main",
  },
};

const PickupPolicyTemplate = ({ t }: { t: any }) => (
  <Box sx={styles.policyContent}>
    <Typography variant="h6" gutterBottom>
      {t("labelPickupOptions")}
    </Typography>
    <Typography paragraph>
      1.{" "}
      <Typography component="span" fontWeight="bold">
        {t("labelCurbsidePickup")}
      </Typography>
      : {t("labelCurbsidePickupDesc")}
    </Typography>
    <Typography paragraph>
      2.{" "}
      <Typography component="span" fontWeight="bold">
        {t("labelLobbyPickup")}
      </Typography>
      : {t("labelLobbyPickupDesc")}
      <Typography component="span" fontWeight="bold">
        $20{t("labelServiceFee")}
      </Typography>
      ,{t("labelParkingFeesDesc")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("labelWaitingTimeCharges")}
    </Typography>
    <Typography paragraph>
      {t("labelWaitingTimeDesc")}{" "}
      <Typography component="span" fontWeight="bold">
        3 {t("labelHours")}
      </Typography>
      ,{t("labelAdditionalChargeDesc")}
      <Typography component="span" fontWeight="bold">
        $40{t("labelPerHour")}
      </Typography>
      {t("labelWillApply")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("labelCancellationPolicies")}
    </Typography>
    <Typography paragraph>
      1. {t("labelCancellationsDesc")}
      <Typography component="span" fontWeight="bold">
        3{t("labelHours")}
      </Typography>
      {t("labelBeforeFlightDesc")}
      <Typography component="span" fontWeight="bold">
        {t("labelFullCharge")}
      </Typography>
      {t("labelOfBooking")}
    </Typography>
    <Typography paragraph>
      2. {t("labelAirlineCancelsDesc")}
      <Typography component="span" fontWeight="bold">
        3{t("labelHoursInAdvance")}
      </Typography>
      {t("labelToAvoidCharges")}
      <Typography component="span" fontWeight="bold">
        50%{t("labelCharge")}
      </Typography>
      {t("labelOfOrder")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("labelImportantFlightInfo")}
    </Typography>
    <Typography paragraph>
      1. {t("labelPassengersMustEnsure")}{" "}
      <Typography component="span" fontWeight="bold">
        {t("labelFlightNumberAccurate")}
      </Typography>
      {t("labelToEnableDriver")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("labelPickupLocationLuggage")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("labelPickupLocationChanges")}
      </Typography>
      : {t("labelChangesToPickup")}
      <Typography component="span" fontWeight="bold">
        {t("labelNotPermitted")}
      </Typography>
      . {t("labelAnyNecessaryAdjustments")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("labelLuggageAllowance")}
      </Typography>
      : {t("labelEachPassenger")}
      <Typography component="span" fontWeight="bold">
        {t("labelTwoPiecesOfLuggage")}
      </Typography>
      , {t("labelSubjectToVehicle")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("labelTollRoadFees")}
    </Typography>
    <Typography paragraph>
      {t("labelTollRoadFeesAre")}
      <Typography component="span" fontWeight="bold">
        {t("labelNotIncluded")}
      </Typography>
      {t("labelInStandardService")}
    </Typography>

    <Divider sx={styles.policyDivider} />
    <Typography variant="body2" color="text.secondary" sx={styles.policyText}>
      {t("labelServiceFeesAndPolicies")}
    </Typography>
  </Box>
);

const DropoffPolicyTemplate = ({ t }: { t: any }) => (
  <Box sx={styles.policyContent}>
    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicyTitle")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionLobby")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyLobbyLabel")}
      </Typography>
      {t("dropoffPolicyLobbyText")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyBoardingLabel")}
      </Typography>
      {t("dropoffPolicyBoardingPrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyBoardingHighlight")}
      </Typography>
      {t("dropoffPolicyBoardingSuffix")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionWaiting")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyWaitingGrace")}
      </Typography>
      {t("dropoffPolicyWaitingGraceSuffix")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyWaitingFeePrefix")}
      <Typography component="span" fontWeight="bold">
        $20
      </Typography>
      {t("dropoffPolicyWaitingFeeMiddle")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyWaitingFeeHighlight")}
      </Typography>
      {t("dropoffPolicyWaitingFeeSuffix")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionChanges")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyChangesPrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyChangesTimeHighlight")}
      </Typography>
      {t("dropoffPolicyChangesMiddle")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyChangesFeeHighlight")}
      </Typography>
      {t("dropoffPolicyChangesSuffix")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyChangesAvailabilityPrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyChangesAvailabilityHighlight")}
      </Typography>
      {t("dropoffPolicyChangesAvailabilitySuffix")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionCancellation")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyPassengerCancellationLabel")}
      </Typography>
      {t("dropoffPolicyPassengerCancellationPrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyPassengerCancellationTime")}
      </Typography>
      {t("dropoffPolicyPassengerCancellationMiddle")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyPassengerCancellationFee")}
      </Typography>
      {t("dropoffPolicyPassengerCancellationSuffix")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyAirlineCancellationLabel")}
      </Typography>
      {t("dropoffPolicyAirlineCancellationPrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyAirlineCancellationTime")}
      </Typography>
      {t("dropoffPolicyAirlineCancellationMiddle")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyAirlineCancellationFee")}
      </Typography>
      {t("dropoffPolicyAirlineCancellationSuffix")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionLuggage")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyLuggagePrefix")}
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyLuggageHighlight")}
      </Typography>
      {t("dropoffPolicyLuggageMiddle")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyLuggageAdditional")}
    </Typography>

    <Typography variant="h6" gutterBottom>
      {t("dropoffPolicySectionToll")}
    </Typography>
    <Typography paragraph>
      <Typography component="span" fontWeight="bold">
        {t("dropoffPolicyTollPrefix")}
      </Typography>
      {t("dropoffPolicyTollMiddle")}
    </Typography>
    <Typography paragraph>
      {t("dropoffPolicyGeneralTerms")}
    </Typography>

    {/* 底部说明 <Divider sx={styles.policyDivider} />
    <Typography variant="body2" color="text.secondary" sx={styles.policyText}>
      {t("dropoffPolicyFooter")}
    </Typography> */}
  </Box>
);

export default function CurrentOrder() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);

  // 从路由状态中获取传递的订单详情
  const passedOrderDetail = location.state?.orderDetail;
  // 从admin过来的访问
  const searchParams = new URLSearchParams(location.search);
  const orderToken = searchParams.get("token");
  const orderId = searchParams.get("orderId");
  const isMobileLandscape = useMediaQuery("(max-width: 1024px) and (orientation: landscape)");
  const shouldUseCompactOrderView = Boolean(orderToken) && isMobileLandscape;

  // const currentOrder = passedOrderDetail;
  const [currentOrder, setCurrentOrder] = useState(passedOrderDetail);
  const [error, setError] = useState<string | null>(null);
  const [creditCardPaymentText, setCreditCardPaymentText] = useState(
    t("labelCardPayment")
  );

  // 如果有 orderId 但没有 passedOrderDetail，则通过 API 获取订单详情
  useEffect(() => {
    if (orderId && orderToken && !passedOrderDetail) {
      setError(null);
      dispatch(searchOrderById({order_id: +orderId, is_guest: false, orderToken: orderToken}))
        .unwrap()
        .then((data: any) => {
          setCurrentOrder(data);
        })
        .catch((error: any) => {
          console.error('获取订单失败:', error);
          setError(error?.message || t("labelOrderLoadError"));
        });
    }
  }, [dispatch, orderId, orderToken, passedOrderDetail, t]);

  useEffect(() => {
    const cachedValue = getSessionStorageWithExpiry<AppConfigI18nData[]>(
      CREDIT_CARD_FEE_SESSION_KEY
    );

    if (cachedValue) {
      setCreditCardPaymentText(
        getCreditCardFeeText(cachedValue, i18n.language, t("labelCardPayment"))
      );
      return;
    }

    const authorization = orderToken || localStorage.getItem("token");

    if (!authorization) {
      setCreditCardPaymentText(t("labelCardPayment"));
      return;
    }

    dispatch(
      getAppConfigI18nByKey({
        key: CREDIT_CARD_FEE_KEY,
        orderToken: orderToken || undefined,
      })
    )
      .unwrap()
      .then((data) => {
        setSessionStorageWithExpiry(
          CREDIT_CARD_FEE_SESSION_KEY,
          data,
          CREDIT_CARD_FEE_SESSION_TTL
        );

        setCreditCardPaymentText(
          getCreditCardFeeText(data, i18n.language, t("labelCardPayment"))
        );
      })
      .catch((fetchError) => {
        console.error("获取 payment.credit_card_fee 失败:", fetchError);
        setCreditCardPaymentText(t("labelCardPayment"));
      });
  }, [dispatch, i18n.language, orderToken, t]);

  if (error) {
    return (
      <Box sx={styles.noOrderContainer}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box sx={styles.noOrderContainer}>
        <Typography>{t("labelNoOrders")}</Typography>
      </Box>
    );
  }

  const contactMethod = currentOrder.contact_method ?? consts.contactMethod.PHONE;
  const contactDetailLabel =
    contactMethod === consts.contactMethod.WECHAT
      ? t("wechatId")
      : contactMethod === consts.contactMethod.LINE
      ? t("lineId")
      : t("phone");
  const contactDetailValue =
    contactMethod === consts.contactMethod.WECHAT
      ? currentOrder.wechat_id
      : contactMethod === consts.contactMethod.LINE
      ? currentOrder.line_id
      : currentOrder.phone;

  return (
    <Box sx={styles.container}>
      <Grid container sx={styles.gridContainer}>
        <Grid item {...styles.leftGrid}>
          <Box sx={styles.leftGridContainer}>
            <Box sx={styles.headerContainer}>
              {/* <IconButton
                sx={styles.backButton}
                onClick={() => navigate(-1)}
                aria-label="back"
              >
                <ArrowBackIcon />
              </IconButton> */}
              <Box
                component="img"
                src={blueskyImage}
                alt="Blue Sky Logo"
                sx={{
                  display: "block",
                  margin: "0 auto",
                  width: { xs: "120px", sm: "150px" },
                  height: "auto",
                  mb: 2,
                }}
              />
              <Typography align="center" variant="h5" sx={styles.title}>
                {currentOrder.service_type ===
                consts.serviceType.AIRPORT_PICK_UP
                  ? t("labelReservedService")
                  : t("labelReservedDropoffService")}
              </Typography>
              {currentOrder.status === consts.orderStatus.CANCELED && (
                <Typography
                  align="center"
                  color="error"
                  variant="h5"
                  sx={{ mb: 2 }}
                >
                  {t("labelBookingCanceled")}
                </Typography>
              )}
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                ...styles.mainTable,
                ...(shouldUseCompactOrderView
                  ? {
                      width: "140%",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                    }
                  : {}),
              }}
            >
              <Table
                size={shouldUseCompactOrderView ? "small" : "medium"}
                sx={
                  shouldUseCompactOrderView
                    ? {
                        minWidth: 760,
                        "& .MuiTableCell-root": {
                          py: 0.75,
                          px: 1,
                          fontSize: "0.75rem",
                          lineHeight: 1.3,
                        },
                      }
                    : undefined
                }
              >
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={styles.headerCell}>
                      {t("orderId2")}
                    </TableCell>
                    <TableCell>
                      {`#${currentOrder.order_id}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.headerCell}>
                      {t("date")}
                    </TableCell>
                    <TableCell>
                      {dayjs(
                        currentOrder.service_type === consts.serviceType.AIRPORT_PICK_UP
                          ? currentOrder.flight_time
                          : currentOrder.use_time
                      )
                        .tz("America/Los_Angeles")
                        .format("MM/DD/YYYY")}
                    </TableCell>
                    <TableCell component="th" sx={styles.headerCell}>
                      {t("week")}
                    </TableCell>
                    <TableCell>
                      {dayjs(
                        currentOrder.service_type === consts.serviceType.AIRPORT_PICK_UP
                          ? currentOrder.flight_time
                          : currentOrder.use_time
                      )
                        .tz("America/Los_Angeles")
                        .format("dddd")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {currentOrder.service_type ===
                      consts.serviceType.AIRPORT_PICK_UP
                        ? t("pickupAirport")
                        : t("dropOffAirport")}
                    </TableCell>
                    <TableCell>{currentOrder.airport}</TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("service")}
                    </TableCell>
                    <TableCell>
                      {currentOrder.service_type ===
                      consts.serviceType.AIRPORT_PICK_UP
                        ? t("pickupService")
                        : t("dropoffService")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("name")}
                    </TableCell>
                    <TableCell>{currentOrder.customer_name}</TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("flightInfo")}
                    </TableCell>
                    <TableCell>{currentOrder.flight_number}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {contactDetailLabel}
                    </TableCell>
                    <TableCell>{contactDetailValue || "--"}</TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("lobbyServiceShort")}
                    </TableCell>
                    <TableCell>{currentOrder.lobby_service ? t("yes") : t("no")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("passengers")}
                    </TableCell>
                    <TableCell>{currentOrder.passengers}</TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {currentOrder.service_type ===
                      consts.serviceType.AIRPORT_PICK_UP
                        ? t("arrivalTime")
                        : t("useTime")}
                    </TableCell>
                    <TableCell>
                      {dayjs(
                        currentOrder.service_type ===
                          consts.serviceType.AIRPORT_PICK_UP
                          ? currentOrder.flight_time
                          : currentOrder.use_time
                      )
                        .tz("America/Los_Angeles")
                        .format("HH:mm")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("luggages")}
                    </TableCell>
                    {/* <TableCell sx={primaryValueCellSx}> */}
                    <TableCell>
                      {currentOrder.luggages ?? "0"}
                    </TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("carryOns")}
                    </TableCell>
                    {/* <TableCell sx={secondaryValueCellSx}> */}
                    <TableCell>
                      {currentOrder.carry_on ?? "0"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("address")}
                    </TableCell>
                    <TableCell colSpan={3} sx={styles.addressCell}>
                      {currentOrder.addr1}
                      {currentOrder.addr2 && `\n${currentOrder.addr2}`}
                      {currentOrder.addr3 && `\n${currentOrder.addr3}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("price")}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // 如果 base_fare 等于 wrongFare，显示客服联系信息
                        if (currentOrder.base_fare === consts.wrongFare) {
                          return t("pleaseContactCustomerService");
                        }

                        // 如果 tips 等于 wrongTips，显示客服联系信息
                        if (currentOrder.tips === consts.wrongTips) {
                          return t("pleaseContactCustomerService");
                        }

                        // const parkingSuffix = currentOrder.lobby_service ? "+P" : "";
                        const parkingSuffix = currentOrder.lobby_service ? "+" + t("labelParkingSuffix") : "";

                        // 如果 tips 不为 0，显示完整的价格信息
                        if (currentOrder.tips && currentOrder.tips !== 0) {
                          return `$${currentOrder.base_fare}` + "+" + `$${currentOrder.tips}` + "("+ t("tips")+")" + parkingSuffix;
                        }

                        // 默认情况：只显示 base_fare + tips 标签
                        return `$${currentOrder.base_fare}` + "+" + t("tips") + parkingSuffix;
                      })()}
                    </TableCell>
                    <TableCell component="th" sx={styles.normalHeaderCell}>
                      {t("note")}
                    </TableCell>
                    <TableCell>{currentOrder.notes || "--"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography align="center" variant="h6" gutterBottom>
              {t("labelPaymentMethods")}
            </Typography>
            <TableContainer component={Paper} sx={styles.smallTable}>
              <Table sx={styles.paymentTable}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={styles.paymentLeftCell}>
                      {t("labelCashPayment")}
                    </TableCell>
                    <TableCell sx={styles.paymentRightCell}>
                      {creditCardPaymentText}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.paymentLastLeftCell}>
                      Venmo: @Blue-Sky111
                      <br />
                      {t("labelVenmo")}
                    </TableCell>
                    <TableCell>
                      Zelle: 626-476-9595
                      <br />
                      {t("labelVenmo")}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={styles.contactFirstCell}>
                    <Typography variant="h6">
                      {t("labelContactInfo")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={styles.contactMiddleCell}>
                    {t("labelOfficeChinese")}: 626-968-2688 /{" "}
                    {t("labelOfficeEnglish")}: 626-968-7100
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={styles.contactMiddleCell}>
                    {t("labelBookingWithText")}: 626-672-5816 / 626-632-0688
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={styles.contactLastCell}>
                    {t("labelOnlineBooking")}:
                    https://bluesky-tour.com/shuttle/
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'left' }}>
              <Button
                variant="contained"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIcon />}
              >
                {t("btnGoBack")}
              </Button>
            </Box>
          </Box>
        </Grid>

        {!shouldUseCompactOrderView && (
          <Grid item {...styles.rightGrid}>
            <Box sx={styles.policyContainer}>
              {currentOrder.service_type ===
              consts.serviceType.AIRPORT_PICK_UP ? (
                <PickupPolicyTemplate t={t} />
              ) : (
                <DropoffPolicyTemplate t={t} />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
